<script setup lang="ts">
import { z } from "zod";
import type { Licencie } from "@carnet/api/isia";
import type { FormSubmitEvent } from "#ui/types";

const { t } = useI18n();

const model = defineModel<Licencie>({ required: true });

const props = defineProps<{
  id?: string;
}>();

const schema = z.object({
  Identifiant: z
    .string()
    .min(1, t("form.validation.identifier_required"))
    .regex(/^[AE]-\d{2}-\d{6,7}$/, t("form.validation.identifier_format")),
});

type Schema = z.output<typeof schema>;

const form = useTemplateRef("form");

const isValid = computed(() => {
  const result = schema.safeParse(model.value);
  return result.success;
});

defineExpose({ isValid });

const emit = defineEmits<{
  submit: [];
}>();

function onSubmit(event: FormSubmitEvent<Schema>) {
  emit("submit");
}
</script>

<template>
  <UForm :id="props.id" ref="form" :schema :state="(model as Record<string, unknown>)" class="flex flex-col grow" :validate-on="[]" @submit="onSubmit">
    <div class="grow not-last:mb-4">
      <div class="not-last:mb-10">
        <UFormField name="Identifiant" :label="$t('login.identifier.label')" class="not-last:mb-7">
          <template #hint>
            <AppInputIdentifierHelper />
          </template>
          <UInput
            v-model="(model.Identifiant as string | undefined)"
            v-maskito="identifierMaskOptions"
            variant="soft"
            data-testid="identifier-input"
            :placeholder="$t('register.identifier.placeholder')"
            @input="form?.clear('Identifiant')"
          />
        </UFormField>
      </div>
      <slot name="errors" />
    </div>
  </UForm>
</template>

<script lang="ts">
import { maskito as vMaskito } from "@maskito/vue";
import type { MaskitoOptions } from "@maskito/core";

const identifierMaskOptions: MaskitoOptions = {
  mask: [...Array(1).fill(/\w/), "-", ...Array(2).fill(/\w/), "-", ...Array(7).fill(/\w/)],
  preprocessors: [
    ({ elementState, data }) => {
      const { value, selection } = elementState;
      return {
        elementState: {
          selection,
          value: value.toUpperCase().replace(" ", "-"),
        },
        data: data.toUpperCase().replace(" ", "-"),
      };
    },
  ],
};
</script>
